import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { toast } from "react-toastify";
import { ButtonBase } from "../../../common/components/form";
import decline from "../../../common/lib/decline.js";
import { useGetPaymentStatus, useReduxAccount } from "../../hooks";
import * as API from "../../lib/api.js";
import { setAccount } from "../../store/panel.js";
import Confirm from "../Confirm";
import OverlayCustom from "../Overlay";
import PaymentMethodChosing from "../Payment/PaymentMethodChoosing.jsx";

export default function Subscription() {
    const { t } = useTranslation();
    const history = useHistory();
    const domFormWrap = React.useRef();
    const { needToPay } = useGetPaymentStatus();

    const [list, setList] = React.useState([]);
    const [overlayShows, setOverlayShows] = React.useState(false);
    const [isUpgrading, setIsUpgrading] = React.useState(false);

    const [confirmUnsubscribe, setConfirmUnsubscribe] = React.useState(false);
    const [paymentMethodChosing, setPaymentMethodChosing] = React.useState(false);
    const [planChangingId, setPlanChangingId] = React.useState(null);

    const { id_plan: selectedPlan, subscription_active, payment_system } = useReduxAccount();
    const dispatch = useDispatch();

    React.useEffect(() => {
        API.PlanList().then(res => {
            if (!res?.ok || !res?.list) {
                return;
            }

            API.AccountCheck().then(account => dispatch(setAccount(account)));
            setList(res.list);
        });
    }, []);

    function onPlanSelect(id) {
        if (subscription_active === false) {
            return getPayment(payment_system, id);
        } else if (selectedPlan > id) {
            if (!payment_system) {
                setPaymentMethodChosing(true);
                setPlanChangingId(id);
                return;
            }
            return getPayment(payment_system, id);
        }

        if (!payment_system) {
            setPaymentMethodChosing(true);
            setPlanChangingId(id);
            return;
        }

        return getPayment(payment_system, id);
    }

    function getPayment(system, id) {
        if (needToPay && id === selectedPlan) {
            API.GetPaymentForm(system)
                .then(({ html }) => {
                    domFormWrap.current.innerHTML = html;
                    let form = domFormWrap.current.querySelector("form");
                    form && form.submit();
                })
                .catch(e => console.warn("[Error at GetPaymentForm]", e));
        } else {
            API.SetPayment(system, id)
                .then(res => {
                    if (!res?.ok) {
                        return;
                    }

                    if (res.html) {
                        domFormWrap.current.innerHTML = res.html;
                        let form = domFormWrap.current.querySelector("form");
                        form && form.submit();
                        return;
                    }

                    API.AccountCheck().then(account => dispatch(setAccount(account)));

                    if (subscription_active !== false) {
                        setIsUpgrading(res.planUpdate);
                        !res.html && !res.subscribeUpdate && setOverlayShows(true);
                    }
                })
                .catch(e => console.warn("onPlanSelect()", e));
        }
    }

    function onUnsubscribe() {
        API.CancelSubscription().then(({ ok }) => {
            if (!ok) {
                toast.error(t("NotSubscribed"));
            }

            onCancel();
        });
    }

    function onUnsubscribeConfirm() {
        setConfirmUnsubscribe(true);
    }

    function onCancel() {
        API.AccountCheck().then(account => dispatch(setAccount(account)));
        history.replace("/subscription");
        setOverlayShows(false);
        setConfirmUnsubscribe(false);
        setPlanChangingId(null);
        setPaymentMethodChosing(false);
    }

    return (
        <>
            <div ref={domFormWrap} style={{ display: "none" }}></div>

            <div className="panel-subscription">
                <h2 className="panel-subscription-header">{t("SelectYourPlan")}</h2>

                <div className="panel-subscription-list">
                    {list.map(({ id, title, parameters }) => (
                        <div key={title} className="panel-subscription-item">
                            <div className="panel-subscription-item-top">
                                <div className="panel-subscription-item-title">{title}</div>

                                <div className="panel-subscription-item-description">{parameters.description}</div>

                                <div className="panel-subscription-item-price">
                                    ${parameters.price}
                                    <div className="panel-subscription-item-period">{t("monthly")}</div>
                                </div>

                                <div className="panel-subscription-item-button">
                                    <ButtonBase
                                        disabled={
                                            subscription_active
                                                ? id === selectedPlan && !needToPay
                                                : subscription_active === false && id !== selectedPlan
                                        }
                                        solid={!parameters.popular}
                                        invert={parameters.popular && selectedPlan !== 4}
                                        onClick={() => onPlanSelect(id)}
                                    >
                                        {subscription_active
                                            ? id > selectedPlan
                                                ? t("Upgrade")
                                                : id === selectedPlan
                                                ? needToPay
                                                    ? t("Pay")
                                                    : t("Selected")
                                                : t("Downgrade")
                                            : subscription_active === false
                                            ? id > selectedPlan
                                                ? t("Upgrade")
                                                : id === selectedPlan
                                                ? t("RestoreSubscribtion")
                                                : t("Downgrade")
                                            : id > selectedPlan
                                            ? t("Upgrade")
                                            : id === selectedPlan
                                            ? needToPay
                                                ? t("Pay")
                                                : t("RestoreSubscribtion")
                                            : t("Downgrade")}
                                    </ButtonBase>
                                </div>
                            </div>

                            <div className="panel-subscription-item-separator"></div>

                            <div className="panel-subscription-item-bottom">
                                <div className="panel-subscription-item-features">
                                    <p>
                                        {parameters.ecommerces || 0}{" "}
                                        {decline(parameters.ecommerces || 0, [
                                            t("1 AI e-commerce pages"),
                                            t("3 AI e-commerce pages"),
                                            t("5 AI e-commerce pages"),
                                        ])}
                                    </p>

                                    <p>
                                        {parameters.websites || 0}{" "}
                                        {decline(parameters.websites || 0, [
                                            t("1 Websites"),
                                            t("3 Websites"),
                                            t("5 Websites"),
                                        ])}
                                    </p>

                                    {parameters.freeDesign && <p>{t("Free design")}</p>}
                                    {parameters.ecommerceAITexts && <p>{t("AI text generation for e-commerce")}</p>}
                                    {parameters.ecommerceAIGoogleAds && <p>{t("AI Google Ad campaign generation")}</p>}
                                    {parameters.seo && <p>{t("SEO optimisation")}</p>}
                                    {parameters.ecommerceAICTR && <p>{t("AI Conversion Rate Optimization")}</p>}
                                    {parameters.amp && <p>{t("AMP adaptation")}</p>}
                                    {parameters.mobile && <p>{t("Mobile adaptation")}</p>}
                                    {parameters.freeHosting && <p>{t("Free Hosting")}</p>}
                                    {parameters.freeAnalytics && <p>{t("Analytic integration")}</p>}
                                    {parameters.cloudHosting && <p>{t("Cloud web hosting")}</p>}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {subscription_active && (
                    <div className="panel-subscription-unsubscribe">
                        <ButtonBase link onClick={() => onUnsubscribeConfirm()}>
                            {t("Unsubscribe")}
                        </ButtonBase>
                    </div>
                )}
            </div>

            {overlayShows && (
                <OverlayCustom>
                    <div className="overlay-payment-logo">
                        <img src="/android-chrome-512x512.png" alt="PGBLDR.com" width={"200px"} />
                    </div>
                    {!isUpgrading && (
                        <div className="overlay-subscription-texts">
                            <p>{t("PlanDowngrade")}</p>
                        </div>
                    )}
                    {isUpgrading && (
                        <div className="overlay-subscription-texts">
                            <p>{t("PlanUpgrade1")}</p>
                            <p>{t("PlanUpgrade2")}</p>
                            <p>{t("PlanUpgrade3")}</p>
                        </div>
                    )}

                    <div className="overlay-payment-buttons">
                        <ButtonBase onClick={onCancel}>{t("Continue")}</ButtonBase>
                    </div>
                </OverlayCustom>
            )}

            <Confirm
                show={confirmUnsubscribe}
                header={t("UnsubscribeConfirmTitle")}
                text={t("UnsubscribeConfirm")}
                actions={[
                    { onClick: onCancel, text: t("Cancel") },
                    {
                        onClick: onUnsubscribe,
                        text: t("Confirm"),
                        props: { invert: true },
                    },
                ]}
            />

            <PaymentMethodChosing
                show={paymentMethodChosing}
                header={t("PaymentMethodChoosing")}
                text={t("PaymentMethodChoosingText")}
                planToChange={list && list[planChangingId - 1]}
                actions={[
                    {
                        onClick: () => getPayment("paypal", planChangingId),
                        text: t("PayPal"),
                        props: { invert: true },
                    },
                    {
                        onClick: () => getPayment("liqpay", planChangingId),
                        text: t("LiqPay"),
                        props: { invert: true },
                    },
                    { onClick: onCancel, text: t("Cancel") },
                ]}
            />
        </>
    );
}
